import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { conditionsAPI, getImageUrl } from "../../services/api";
import { useBookingModal } from "../../context/BookingModalContext";
import s1 from "../../assets/img/s1.jpg";
import "./ConditionDetail.scss";

const buildDefaultContent = (title) => {
  const name = (title || "this condition").toLowerCase();
  return [
    `Our experienced physiotherapists provide thorough assessment and treatment for ${name}. We combine hands-on therapy, targeted exercise programs and patient education to help you recover safely and effectively.`,
    `During your first visit, our team will take a detailed history and conduct a physical assessment to identify the underlying cause of your symptoms. From there, we'll build a personalised treatment plan based on your specific needs, goals and lifestyle.`,
    `Treatment may include manual therapy, individualised exercise rehabilitation, posture and movement education, and ongoing progress reviews to keep you on track.`,
    `If you're dealing with ${name}, don't wait for it to get worse. Book an appointment with our team today and take the first step toward feeling better.`,
  ];
};

const ConditionDetail = () => {
  const { id } = useParams();
  const [condition, setCondition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { openModal } = useBookingModal();

  useEffect(() => {
    const fetchCondition = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await conditionsAPI.getById(id);
        setCondition(response.data);
      } catch (err) {
        setError(err.response?.status === 404 ? "Condition not found" : "Failed to load condition");
      } finally {
        setLoading(false);
      }
    };
    fetchCondition();
  }, [id]);

  if (loading) {
    return (
      <div className="condition-detail-loading">
        <div className="loading-message">Loading...</div>
      </div>
    );
  }

  if (error || !condition) {
    return (
      <div className="condition-detail-error">
        <div className="error-message">
          <h2>Condition Not Found</h2>
          <p>{error || "The condition you are looking for does not exist."}</p>
          <Link to="/conditions" className="site__button site__button_style--solid site__button_shape--default">
            <span className="text">Back to Conditions</span>
          </Link>
        </div>
      </div>
    );
  }

  const paragraphs = condition.content
    ? condition.content.split(/\n+/).filter(Boolean)
    : buildDefaultContent(condition.title);

  return (
    <div className="main main_condition_detail">
      <div className="page-hero">
        <div className="page-hero__content">
          <span className="small__text">WE TREAT</span>
          <h1 className="headline__text">{condition.title}</h1>
          {condition.description && (
            <p className="page-hero__subtitle">{condition.description}</p>
          )}
        </div>
      </div>

      <div className="condition-detail-section">
        <div className="condition-detail-container">
          <Link to="/conditions" className="back-to-conditions">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to Conditions
          </Link>

          <div className="condition-detail-image">
            <img
              src={condition.image ? getImageUrl(condition.image) : s1}
              alt={condition.title}
              onError={(e) => { e.target.src = s1; }}
            />
          </div>

          <div className="condition-detail-content">
            {paragraphs.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <div className="condition-detail-cta">
            <h2>Ready to start your recovery?</h2>
            <p>Book an appointment and our physiotherapists will assess your condition and build a personalised treatment plan.</p>
            <button
              type="button"
              onClick={() => setTimeout(openModal, 0)}
              className="site__button site__button_style--solid site__button_shape--default"
            >
              <span className="text">Book an Appointment</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConditionDetail;
